// Class for the remove button in the cart
class CartRemoveButton extends HTMLElement {
  constructor() {
    super();
    // Add click event listener to the button
    this.addEventListener('click', this.onClick.bind(this));
  }

  // Handle the click event
  onClick(event) {
    event.preventDefault();

    // Find the cart form or cart drawer content element
    const cartItems = this.closest('cart-form') || this.closest('cart-drawer-content');
    // Update the quantity of the item to 0
    cartItems.updateQuantity(this.dataset.index, 0);
  }
}

customElements.define('cart-remove-button', CartRemoveButton);

// Class for the cart form
class CartForm extends HTMLElement {
  constructor() {
    super();

    // Initialize the debouncedOnChange function with a debounce of 300ms
    this.debouncedOnChange = debounce((event) => {
      this.onChange(event);
    }, 300);

    // Add change event listener to the form
    this.addEventListener('change', this.debouncedOnChange.bind(this));

    // Get references to important elements
    this.lineItemStatusElement = document.getElementById('Cart-LineItemStatus');
    this.cartStatus = document.getElementById('Cart-LiveRegionText');

    // Calculate the current item count
    this.currentItemCount = Array.from(this.querySelectorAll('[name="updates[]"]'))
      .reduce((total, quantityInput) => total + parseInt(quantityInput.value), 0);
  }

  // Handle the change event
  onChange(event) {
    // Check if the change is related to the cart note
    if (event.target.id === 'Cart-Note' || event.target.id === 'DrawerCart-Note') {
      // Update the cart note
      const body = JSON.stringify({ note: event.target.value });
      fetch(`${routes.cart_update_url}`, { ...fetchConfig(), ...{ body } });
      return;
    }

    // Update the quantity of the item
    this.updateQuantity(
      event.target.dataset.index,
      event.target.value,
      document.activeElement.getAttribute('name')
    );
  }

  // Update the quantity of an item
  updateQuantity(line, quantity, name) {
    // Enable loading state
    this.enableLoading();

    const body = JSON.stringify({
      line,
      quantity,
      sections: this.getSectionsToRender().map((section) => section.section),
      sections_url: window.location.pathname
    });

    fetch(`${routes.cart_change_url}`, { ...fetchConfig(), ...{ body } })
      .then((response) => {
        return response.text();
      })
      .then((state) => {
        const parsedState = JSON.parse(state);
        const quantityElement = document.getElementById(`Quantity-${line}`) || document.getElementById(`Cart-Quantity-${line}`);
        const items = document.querySelectorAll('.cart-item');

        if (parsedState.errors) {
          // Restore the previous value
          quantityElement.value = quantityElement.getAttribute('value');
          this.updateLiveRegions(line, parsedState.errors);
          return;
        }

        this.classList.toggle('is-empty', parsedState.item_count === 0);

        // Update the sections in the cart
        this.getSectionsToRender().forEach((section => {
          if (section.id == "Cart") {
            const elementsToReplace = document.getElementById(section.id).querySelectorAll(section.selector);
            const mainCartParsed = parsedState.sections[document.getElementById('Cart').dataset.id];
            elementsToReplace.forEach((el) => {
              el.innerHTML = this.getSectionInnerHTMLWithDataset(mainCartParsed, section.selector, el.dataset.content);
            });
            return;
          }
          const elementToReplace =
            document.getElementById(section.id).querySelector(section.selector) || document.getElementById(section.id);
          elementToReplace.innerHTML =
            this.getSectionInnerHTML(parsedState.sections[section.section], section.selector);
        }));

        const updatedValue = parsedState.items[line - 1] ? parsedState.items[line - 1].quantity : undefined;
        let message = '';
        if (items.length === parsedState.items.length && updatedValue !== parseInt(quantityElement.value)) {
          if (typeof updatedValue === 'undefined') {
            message = window.cartStrings.error;
          } else {
            message = window.cartStrings.quantityError.replace('[quantity]', updatedValue);
          }
        }
        this.updateLiveRegions(line, message);
      }).catch(() => {
        const errors = document.getElementById('Cart-Errors');
        errors.textContent = window.cartStrings.error;
      }).finally(() => {
        this.disableLoading();
      });
  }


  // Enable loading state
  enableLoading() {
    const cart = document.getElementById('Cart') || document.getElementById('CartDrawer');
    cart.classList.add('disable-pointer-events');
  }

  // Disable loading state
  disableLoading() {
    const cart = document.getElementById('Cart') || document.getElementById('CartDrawer');
    cart.classList.remove('disable-pointer-events');
  }

  // Update the live regions for accessibility
  updateLiveRegions(line, message) {
    const lineItemError = document.getElementById(`Cart-LineItemError-${line}`);

    if (lineItemError) lineItemError.querySelector('small').innerHTML = message;

    this.lineItemStatusElement.setAttribute('aria-hidden', true);
    this.cartStatus.setAttribute('aria-hidden', false);

    // Hide the cart status after 1 second
    setTimeout(() => {
      this.cartStatus.setAttribute('aria-hidden', true);
    }, 1000);
  }

  // Get the sections to render
  getSectionsToRender() {
    return [
      {
        id: 'Cart',
        section: document.getElementById('Cart').dataset.id,
        selector: '.js-contents',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section'
      }
    ];
  }

  // Get the inner HTML of a section
  getSectionInnerHTML(html, selector) {
    return new DOMParser()
      .parseFromString(html, 'text/html')
      .querySelector(selector).innerHTML;
  }

  // Get the inner HTML of a section with a specific dataset
  getSectionInnerHTMLWithDataset(html, selector, dataset) {
    return new DOMParser()
      .parseFromString(html, 'text/html')
      .querySelector(`[data-content="${dataset}"]`).innerHTML;
  }
}

// Define the custom element for the cart form
customElements.define('cart-form', CartForm);

// Define the custom element for the cart free shipping bar
if (!customElements.get('cart-free-shipping-bar')) {
  customElements.define('cart-free-shipping-bar', class CartFreeShippingBar extends HTMLElement {
    static get observedAttributes() {
      return ["threshold", "cart-total"];
    }
    constructor() {
      super();

      // Get references to important elements
      this.text = this.querySelector('.free-shipping-text');
      this.progressBar = this.querySelector('.free-shipping-bar span');
    }

    connectedCallback() {
      // Calculate the threshold based on the currency rate
      this.threshold = Math.round(this.threshold * (Shopify.currency.rate || 1));
      this.updateShippingIndicator();
      this.updateText();
    }

    // Getters and setters for attributes
    get amountLeft() {
      return this.getAttribute('amount-left');
    }

    set amountLeft(value) {
      this.setAttribute('amount-left', value);
    }

    get threshold() {
      return parseFloat(this.getAttribute('threshold'));
    }

    set threshold(value) {
      this.setAttribute('threshold', value);
    }

    get cartTotal() {
      return parseFloat(this.getAttribute('cart-total'));
    }

    set cartTotal(value) {
      this.setAttribute('cart-total', value);
    }

    // Update the shipping indicator progress bar
    updateShippingIndicator() {
      const percentage = Math.round(this.cartTotal * 100.0 / this.threshold);
      // Limit the percentage value to 100 if it exceeds that value
      const clampedPercentage = Math.min(percentage, 100);

      this.progressBar.style.setProperty("--width", `${clampedPercentage}%`);
    }

    // Update the text based on the cart total and threshold
    updateText() {
      if (this.cartTotal >= this.threshold) {
        this.text.innerHTML = this.dataset.unlockedMessage;
      } else {
        this.text.innerHTML = this.dataset.amountLeftMessage.replace('{{ amount }}', this.amountLeft);
      }
    }
  });
}
